import { useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useDemoMode } from '@/features/demo/useDemoMode';
import { useCanOpenPath } from '@/contexts/ScreenSubjectContext';

/**
 * Per-session claim for the aha spotlight. Exported so flows that navigate the
 * user somewhere deliberately (the onboarding wizard's completion step) can
 * claim it up front and keep this hook from overriding that choice.
 */
export const AHA_SESSION_KEY = 'demo-aha-shown';

/**
 * On the user's first session visit (when demo mode is active and there is an
 * aha demo call ID), navigate them once to /records?id=<ahaCallId> to spotlight
 * the call. Per-session via sessionStorage. Guarded against re-firing on the
 * record itself. Not for a grant holder the route gate refuses `/records`
 * (#1703, #1336): the navigation would bounce it to `/dashboard`, and the
 * session flag stays unspent.
 */
export function useFirstLoginRedirect(): void {
  const { isDemoActive, ahaCallId, isLoading } = useDemoMode();
  const navigate = useNavigate();
  const location = useLocation();
  const recordsOpen = useCanOpenPath()('/records');

  useEffect(() => {
    // Wait for the demo-mode decision. `isDemoActive` is `hasDemoData && no real
    // data`, where "real data" comes from /status (`hasRealData`) and, for a
    // status that predates that field, from the viewer-scoped records count. While
    // that decision is still in flight, `isDemoActive` can read true for an org
    // that has real records, and we'd yank the user onto the demo call.
    if (isLoading) return;
    if (!isDemoActive || !ahaCallId) return;
    if (!recordsOpen) return;
    if (sessionStorage.getItem(AHA_SESSION_KEY) === '1') return;

    // Already on the aha record — don't re-navigate.
    const currentId =
      typeof location.search === 'object' && location.search !== null
        ? (location.search as Record<string, unknown>).id
        : undefined;
    if (location.pathname === '/records' && currentId === ahaCallId) {
      sessionStorage.setItem(AHA_SESSION_KEY, '1'); // still flip so we don't keep checking
      return;
    }

    sessionStorage.setItem(AHA_SESSION_KEY, '1');
    void navigate({ to: '/records', search: { id: ahaCallId } as never });
  }, [
    isLoading,
    isDemoActive,
    ahaCallId,
    recordsOpen,
    location.pathname,
    location.search,
    navigate,
  ]);
}
